'use client';

import { useCallback, useState } from 'react';
import {
  NotificationHubStorage,
  NotificationHubConfig as NotificationHubConfigReturn,
} from './Notification.types';
import { notification } from 'antd';

const defaultStorage: NotificationHubStorage = {
  audioEnabled: true,
  balloonEnabled: true,
  notificationPermissionEnabled: false,
  notificationPermissionType: 'default',
};

const STORAGE_NOTIFICATION_HUB_CONFIG_KEY = 'NotificationHubConfig';

export class NotificationHubConfig {
  storage: NotificationHubStorage = defaultStorage;

  constructor() {
    this.getStorage();
  }

  getStorage() {
    if (typeof window === 'undefined' || !('localStorage' in window)) return;

    const storage = localStorage.getItem(STORAGE_NOTIFICATION_HUB_CONFIG_KEY);

    const result = storage ? JSON.parse(storage) : defaultStorage;

    this.storage = {
      ...result,
      notificationPermissionType:
        'Notification' in window
          ? Notification.permission || 'default'
          : 'default',
    };
  }

  setStorage(value: NotificationHubStorage) {
    localStorage.setItem(
      STORAGE_NOTIFICATION_HUB_CONFIG_KEY,
      JSON.stringify(value),
    );
  }
}

export function useNotificationHubConfig(): NotificationHubConfigReturn {
  const notificationConfig = new NotificationHubConfig();

  const [storage, handleStorage] = useState<NotificationHubStorage>(
    () => notificationConfig.storage,
  );

  const toggleAudio = () => {
    handleStorage((prev) => {
      const next = { ...prev, audioEnabled: !prev.audioEnabled };
      notificationConfig.setStorage(next);
      return next;
    });
  };

  const toggleBalloon = () => {
    handleStorage((prev) => {
      const next = { ...prev, balloonEnabled: !prev.balloonEnabled };
      notificationConfig.setStorage(next);
      return next;
    });
  };

  const toggleNotificationPermission = useCallback(() => {
    const handleNotificationPermission = (
      notificationPermissionEnabled: boolean,
      notificationPermissionType: NotificationHubStorage['notificationPermissionType'],
    ) => {
      handleStorage((prev) => {
        const next = {
          ...prev,
          notificationPermissionEnabled,
          notificationPermissionType,
        };
        notificationConfig.setStorage(next);
        return next;
      });
    };

    if (!('Notification' in window) && !storage.notificationPermissionEnabled) {
      notification.warning({
        message: 'Configuração de notificação',
        description:
          'Este dispositivo não possui suporte a notificação de sistema',
      });

      return;
    }

    if (!('Notification' in window)) return;

    if (Notification.permission === 'granted') {
      handleNotificationPermission(
        !storage.notificationPermissionEnabled,
        'granted',
      );
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        handleNotificationPermission(permission === 'granted', permission);
      });
    } else {
      handleNotificationPermission(false, 'denied');
    }
  }, [storage]);

  return {
    ...storage,
    toggleAudio,
    toggleBalloon,
    toggleNotificationPermission,
  };
}
